import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiFeather } from "react-icons/pi";
import { Col, Input, Row } from 'reactstrap';
import { Switch, Select } from 'antd';
import { CKEditor } from 'ckeditor4-react';
import { ToSeoUrl } from '../general/SeoUrl';
import { URL as API_URL } from '../../api';

const CMSForm = ({ modal, setFormData, formData, seoFormData, setSeoFormData, readOnlyFields, formValues }) => {
    const { t } = useTranslation();
    const editorRefs = useRef({});

    useEffect(() => {
        if (modal) {
            if (!formData.file) {
                setFormData(prev => ({ ...prev, file: [] }));
            } else if (!Array.isArray(formData.file)) {
                setFormData(prev => ({ ...prev, file: [formData.file] }));
            }
        }
    }, [modal, formData, setFormData]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const existingFiles = Array.isArray(formData.file) ? formData.file : (formData.file ? [formData.file] : []);
            setFormData({ ...formData, file: [...existingFiles, ...files] });
        }
    };

    const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateValue;
        }
        const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    };

    const handleSelectChange = (value, key) => {
        setFormData({
            ...formData,
            [key]: value
        });
    };

    const handleEditorChange = (key, evt) => {
        setFormData(prevData => ({
            ...prevData,
            [key]: evt.editor.getData()
        }));
    };

    const handleEditorInstance = (key, editor) => {
        editorRefs.current[key] = editor;
    };

    useEffect(() => {
        Object.keys(editorRefs.current).forEach(key => {
            if (
                editorRefs.current[key] &&
                formData[key] !== undefined &&
                editorRefs.current[key].getData() !== formData[key]
            ) {
                editorRefs.current[key].setData(formData[key] || '');
            }
        });
    }, [formData]);

    return (
        <Row>
            {
                formValues.map((item, index) => (
                    <Col key={index} md={item.col} className='mb-1'>
                        <div className='form-item'>
                            <div className={item.type === "switch" ? "w-100 text-center" : ""}><label>{item.label}</label></div>
                            {item.type !== "file" && item.type !== "switch" && item.type !== "select" && item.type !== "ck" ? <PiFeather size={20} className='color4' /> : null}

                            {item.type === 'file' ? (
                                <div>
                                    {Array.isArray(formData[item.key]) && formData[item.key].length > 0 && (
                                        <div className="mb-2 d-flex flex-wrap">
                                            {formData[item.key].map((file, idx) => {
                                                console.log(file);
                                                const isFileObj = typeof file === "object" && file.type;
                                                const isImage = isFileObj
                                                    ? file.type.startsWith("image/")
                                                    : typeof file === "string" && (
                                                        file.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i)
                                                        || file.startsWith("/")
                                                    );

                                                if (!isImage) return null;

                                                const src = isFileObj
                                                    ? URL.createObjectURL(file)
                                                    : file.startsWith("http") || file.startsWith("https")
                                                        ? file
                                                        : API_URL.replace(/\/$/, "") + (file.startsWith("/") ? file : "/" + file);

                                                return (
                                                    <img
                                                        key={idx}
                                                        src={src}
                                                        alt={file.name || `image-${idx}`}
                                                        style={{ height: 100, objectFit: "cover", marginRight: 8, borderRadius: 4, border: "1px solid #eee" }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}
                                    <Input
                                        id={item.key}
                                        name={item.key}
                                        multiple={true}
                                        type="file"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            ) : item.type === 'date' ? (
                                <Input
                                    id={item.key}
                                    name={item.key}
                                    value={formatDateForInput(formData[item.key])}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setFormData({
                                            ...formData,
                                            [item.key]: newValue || null
                                        });
                                    }}
                                    type="date"
                                />
                            ) : item.type === 'select' ? (
                                <Select
                                    id={item.key}
                                    value={formData[item.key]}
                                    onChange={(value) => handleSelectChange(value, item.key)}
                                    style={{ width: '100%' }}
                                    placeholder={t('select_option')}
                                    options={item.options || []}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                />
                            ) : item.type === 'switch' ? (
                                <div className="d-flex justify-content-center align-items-center mt-2">
                                    <Switch
                                        id={item.key}
                                        checked={!!formData[item.key]}
                                        onChange={(checked) => {
                                            setFormData({
                                                ...formData,
                                                [item.key]: checked
                                            });
                                        }}
                                        size="default"
                                    />
                                </div>
                            ) : item.type === 'ck' ? (
                                <CKEditor
                                    key={`editor-${item.key}`}
                                    data={formData[item.key] || ''}
                                    onChange={evt => handleEditorChange(item.key, evt)}
                                    onInstanceReady={evt => handleEditorInstance(item.key, evt.editor)}
                                    config={{
                                        contentsCss: [
                                            'body { background-color:rgb(222, 226, 233) !important; }'
                                        ],
                                        language: 'tr',
                                        toolbar: [
                                            { name: 'document', items: ['Source', '-', 'Save', 'NewPage', 'Preview', 'Print', '-', 'Templates'] },
                                            { name: 'clipboard', items: ['Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', '-', 'Undo', 'Redo'] },
                                            { name: 'editing', items: ['Find', 'Replace', '-', 'SelectAll', '-', 'Scayt'] },
                                            { name: 'forms', items: ['Form', 'Checkbox', 'Radio', 'TextField', 'Textarea', 'Select', 'Button', 'ImageButton', 'HiddenField'] },
                                            '/',
                                            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript', '-', 'RemoveFormat'] },
                                            { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', 'CreateDiv', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock', '-', 'BidiLtr', 'BidiRtl', 'Language'] },
                                            { name: 'links', items: ['Link', 'Unlink', 'Anchor'] },
                                            { name: 'insert', items: ['Image', 'Flash', 'Table', 'HorizontalRule', 'Smiley', 'SpecialChar', 'PageBreak', 'Iframe'] },
                                            '/',
                                            { name: 'styles', items: ['Styles', 'Format', 'Font', 'FontSize'] },
                                            { name: 'colors', items: ['TextColor', 'BGColor'] },
                                            { name: 'tools', items: ['Maximize', 'ShowBlocks'] }
                                        ],
                                        extraPlugins: 'colorbutton,colordialog',
                                        height: 200,
                                    }}
                                />
                            ) : item.type === 'textarea' ? (
                                <Input
                                    id={item.key}
                                    name={item.key}
                                    value={formData[item.key] || ''}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        [item.key]: e.target.value
                                    })}
                                    type={item.type}
                                    rows={4}
                                />
                            ) : (
                                <Input
                                    id={item.key}
                                    name={item.key}
                                    value={formData[item.key] || ''}
                                    readOnly={readOnlyFields && readOnlyFields.includes(item.key)}
                                    onChange={(e) => {
                                        if (readOnlyFields && readOnlyFields.includes(item.key)) return;

                                        if (item.key === "pagetr") {
                                            setFormData({
                                                ...formData,
                                                [item.key]: e.target.value,
                                                slugtr: ToSeoUrl(e.target.value)
                                            });
                                        } else if (item.key === "pageen") {
                                            setFormData({
                                                ...formData,
                                                [item.key]: e.target.value,
                                                slugen: ToSeoUrl(e.target.value)
                                            });
                                        } else {
                                            setFormData({
                                                ...formData,
                                                [item.key]: e.target.value
                                            });
                                        }
                                    }}
                                    type={item.type}
                                />
                            )}
                        </div>
                    </Col>
                ))
            }
        </Row>
    );
};

export default CMSForm;